/**
 * The Firebase door to a dancer's bag group (packet BG-10): her Jr Staff
 * email account, or her promoted dancer login, asks
 * netlify/functions/bagGroupView.mjs for the same view her cookie session
 * gets from the dancer portal. Read only; nothing here writes.
 *
 * One request per sign-in per page load: the menu, the home card and the
 * page all ask, so the answer is shared for a short while instead of fetched
 * three times. A refresh asks again.
 *
 * THE SHARED ANSWER BELONGS TO ONE SIGN-IN (refute round 1, 2026-10-19). The
 * cache is keyed by uid, but a key is only a label: the request itself goes
 * out as auth.currentUser. So a uid that is not the signed-in one is refused
 * rather than answered, and every cached view is dropped the moment the
 * signed-in account changes or signs out, so one sign-in's group never shows
 * under another's on a shared device.
 */
import { useEffect, useState } from 'react';
import { auth } from './firebase';
import { functionHeaders } from './functionSecurity';
import { EMPTY_BAG_GROUP_VIEW, parseBagGroupView, type BagGroupView } from './bagGroupViewData';

const MESSAGES: Record<string, string> = {
  sign_in_required: 'Sign in again, then open your bag group once more.',
  invalid_token: 'Sign in again, then open your bag group once more.',
  origin_invalid: 'Refresh the page and try again.',
  not_allowed: 'This account cannot open a bag group right now.',
  not_dancer_sign_in: 'There is no bag group to show for this account.',
  too_many_requests: 'Too many tries too quickly. Wait a moment and try again.',
  server_not_configured: 'The studio server is not set up for this yet.',
  'missing app check token': 'This device could not be verified. Refresh and try again.',
  'invalid app check token': 'This device could not be verified. Refresh and try again.',
};

const SHARE_MS = 60_000;
const cache = new Map<string, { at: number; promise: Promise<BagGroupView> }>();
let watchingAuth = false;
let watchedUid: string | null = null;

/** Drop every shared view when the signed-in account changes. Set up once. */
function watchSignIn(): void {
  if (watchingAuth || !auth) return;
  watchingAuth = true;
  watchedUid = auth.currentUser?.uid ?? null;
  auth.onAuthStateChanged((user) => {
    const next = user?.uid ?? null;
    if (next === watchedUid) return;
    watchedUid = next;
    cache.clear();
  });
}

async function fetchView(uid: string): Promise<BagGroupView> {
  const user = auth?.currentUser;
  if (!user || user.uid !== uid) throw new Error(MESSAGES.sign_in_required);
  let response: Response;
  try {
    response = await fetch('/.netlify/functions/bagGroupView', {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: await functionHeaders(user),
      body: '{}',
    });
  } catch {
    throw new Error('Could not reach the studio server. Try again in a moment.');
  }
  const payload = (await response.json().catch(() => ({}))) as { ok?: boolean; view?: unknown; error?: string };
  if (!response.ok || payload.ok !== true) {
    throw new Error(MESSAGES[payload.error ?? ''] ?? 'Your bag group did not load. Try again.');
  }
  return parseBagGroupView(payload.view);
}

/** The view for whoever is signed in, shared for a minute. */
export function getBagGroupViewForSignIn(uid: string, fresh = false): Promise<BagGroupView> {
  return getBagGroupViewForSignInAt(uid, fresh).then((answer) => answer.view);
}

/**
 * The same shared view, with WHEN it was asked (this device's clock): a
 * shared answer reports the ask it came from, not the moment it was handed
 * out again, for the menu pins auto-clear (lib/menuPinsSettled.ts).
 */
export function getBagGroupViewForSignInAt(uid: string, fresh = false): Promise<{ view: BagGroupView; askedAt: number }> {
  watchSignIn();
  if (!uid || auth?.currentUser?.uid !== uid) {
    cache.clear();
    return Promise.reject(new Error(MESSAGES.sign_in_required));
  }
  const held = cache.get(uid);
  if (!fresh && held && Date.now() - held.at < SHARE_MS) {
    const askedAt = held.at;
    return held.promise.then((view) => ({ view, askedAt }));
  }
  const askedAt = Date.now();
  const promise = fetchView(uid);
  cache.set(uid, { at: askedAt, promise });
  promise.catch(() => {
    if (cache.get(uid)?.promise === promise) cache.delete(uid);
  });
  return promise.then((view) => ({ view, askedAt }));
}

/**
 * The view as React state. Disabled (no request at all) unless the caller
 * has already decided this sign-in is the dancer herself.
 */
export function useSignInBagGroupView(uid: string | null | undefined, enabled: boolean): {
  view: BagGroupView;
  loading: boolean;
  error: string | null;
  reload: () => void;
  /** When the view in hand was asked for (this device's clock); 0 until there is one. */
  askedAt: number;
} {
  const [state, setState] = useState<{ key: string; view: BagGroupView; loading: boolean; error: string | null; askedAt: number }>(
    { key: '', view: EMPTY_BAG_GROUP_VIEW, loading: enabled, error: null, askedAt: 0 }
  );
  const [round, setRound] = useState(0);
  const key = enabled && uid ? `${uid}:${round}` : '';
  useEffect(() => {
    if (!key || !uid) {
      setState({ key: '', view: EMPTY_BAG_GROUP_VIEW, loading: false, error: null, askedAt: 0 });
      return;
    }
    let live = true;
    setState((current) => ({ ...current, key, loading: true, error: null }));
    getBagGroupViewForSignInAt(uid, round > 0).then(
      ({ view, askedAt }) => { if (live) setState({ key, view, loading: false, error: null, askedAt }); },
      (error: unknown) => {
        if (live) setState({ key, view: EMPTY_BAG_GROUP_VIEW, loading: false, error: error instanceof Error ? error.message : 'Your bag group did not load. Try again.', askedAt: 0 });
      },
    );
    return () => { live = false; };
  }, [key, uid, round]);
  const settled = state.key === key;
  return {
    view: settled ? state.view : EMPTY_BAG_GROUP_VIEW,
    loading: Boolean(key) && (!settled || state.loading),
    error: settled ? state.error : null,
    reload: () => setRound((value) => value + 1),
    askedAt: settled && !state.loading && !state.error ? state.askedAt : 0,
  };
}
